import React, { useState } from 'react';
import { PageHeader } from "@/components/ui/page-header";
import { CoverLetterUpload } from "@/components/CoverLetterUpload";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { CoverLetterService, type CoverLetterData } from "@/services/coverLetterService";
import { Download, FileText, FileUp, Coins } from "lucide-react";
import { useCredits } from "@/hooks/useCredits";
import { useAuth } from "@/hooks/useAuth";
import { UpgradePrompt } from "@/components/UpgradePrompt";

export default function CoverLetterBuilder() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { balance, deductCredits, isLoading: creditsLoading } = useCredits();
  const [templateUrl, setTemplateUrl] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [jsonInput, setJsonInput] = useState<string>("");
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  const COVER_LETTER_CREDIT_COST = 2;
  
  const [headerInfo, setHeaderInfo] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    cityST: '',
    linkedin: '',
    companyName: '',
    recipientName: '',
    jobTitle: ''
  });
  
  const [letterBody, setLetterBody] = useState(`I am writing to express my enthusiastic interest in this role. After carefully reviewing the job description, I believe I am a perfect match for this opportunity.

I was drawn to your organization by its commitment to excellence and innovative approach. I am eager to contribute my expertise to the success of such a forward-thinking organization.

In my experience, I have developed strong skills and expertise that align with your requirements. I have a track record of delivering results and am passionate about contributing to your team's success.

What sets me apart is my dedication to excellence and strong problem-solving abilities. My commitment to continuous improvement has consistently delivered positive results.

I look forward to the opportunity to meet with you and discuss how my skills and experience align with your goals for this position.

Thank you for considering my application.

Sincerely,

[Your Full Name]`);

  const handleTemplateUploaded = (url: string) => {
    setTemplateUrl(url);
    toast({
      title: "Template ready",
      description: "Your template is now ready for cover letter generation",
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setHeaderInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJsonImport = () => {
    try {
      const parsedData = JSON.parse(jsonInput);
      
      // Update header info if provided
      if (parsedData.headerInfo) {
        setHeaderInfo(prev => ({
          ...prev,
          ...parsedData.headerInfo
        }));
      }
      
      // Update letter body if provided
      if (parsedData.letterBody) {
        setLetterBody(parsedData.letterBody);
      }
      
      // Update template URL if provided
      if (parsedData.templateUrl) {
        setTemplateUrl(parsedData.templateUrl);
      }
      
      toast({
        title: "JSON imported successfully",
        description: "Form fields have been populated with the JSON data",
      });
      
      setJsonInput(""); // Clear the JSON input after successful import
    } catch (error) {
      toast({
        title: "Invalid JSON",
        description: "Please check your JSON format and try again",
        variant: "destructive"
      });
    }
  };

  const getJsonTemplate = () => {
    return JSON.stringify({
      headerInfo: {
        fullName: "John Doe",
        phoneNumber: "(555) 123-4567",
        email: "john.doe@email.com",
        cityST: "New York, NY",
        linkedin: "https://linkedin.com/in/johndoe",
        companyName: "Target Company Inc",
        recipientName: "Jane Smith",
        jobTitle: "Software Engineer"
      },
      letterBody: "I am writing to express my interest in this position...",
      templateUrl: "optional-template-url"
    }, null, 2);
  };

  const generateCoverLetter = async () => {
    // Validate required fields
    if (!headerInfo.fullName || !headerInfo.email || !headerInfo.phoneNumber) {
      toast({
        title: "Missing information",
        description: "Please fill in at least your name, email, and phone number",
        variant: "destructive"
      });
      return;
    }

    if (!letterBody.trim()) {
      toast({
        title: "Missing content",
        description: "Please enter the cover letter body content",
        variant: "destructive"
      });
      return;
    }

    // Check credits
    if (user && balance < COVER_LETTER_CREDIT_COST) {
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);

    try {
      // Credits will be deducted AFTER successful generation
      const coverLetterData: CoverLetterData = {
        headerInfo,
        letterBody,
        templateUrl: templateUrl || undefined
      };

      const blob = await CoverLetterService.generateCoverLetter(coverLetterData);
      
      // Download the generated cover letter
      const nameParts = headerInfo.fullName.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      const jobTitle = headerInfo.jobTitle || 'Position';
      const fileName = `${firstName} ${lastName} - ${jobTitle.trim()} Cover Letter 2025.docx`;
      CoverLetterService.downloadBlob(blob, fileName);

      // NOW deduct credits after successful generation and download
      if (user) {
        const result = await deductCredits('cover_letter_generation', { 
          jobTitle: headerInfo.jobTitle,
          companyName: headerInfo.companyName,
          fileName
        });
        if (result.success) {
          toast({
            title: "Cover letter generated",
            description: `Downloaded successfully. ${result.credits_used || 1} credit used.`,
          });
        } else {
          toast({
            title: "Cover letter generated",
            description: "Downloaded successfully. Note: Credit deduction failed.",
          });
        }
      } else {
        toast({
          title: "Cover letter generated",
          description: "Your cover letter has been generated and downloaded",
        });
      }

    } catch (error) {
      console.error('Error generating cover letter:', error);
      toast({
        title: "Generation failed",
        description: error instanceof Error ? error.message : "Failed to generate cover letter",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <PageHeader
        title="Cover Letter Builder"
        description="Upload templates and generate professional cover letters"
        backTo="/"
      />

      <div className="max-w-4xl mx-auto mb-6">
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Need to generate multiple letters?</h3>
                <p className="text-muted-foreground">Use our Bulk Letter Generator to create cover letters, thank you letters, and outreach letters for multiple resumes at once.</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/bulk-letter-generator'}
                className="ml-4"
              >
                Bulk Generator
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upload">Upload Template</TabsTrigger>
            <TabsTrigger value="json">JSON Import</TabsTrigger>
            <TabsTrigger value="generate">Generate Cover Letter</TabsTrigger>
          </TabsList>

          <TabsContent value="upload">
            <CoverLetterUpload onTemplateUploaded={handleTemplateUploaded} />
          </TabsContent>

          <TabsContent value="json">
            <Card>
              <CardHeader>
                <CardTitle>Import from JSON</CardTitle>
                <CardDescription>
                  Paste JSON data to automatically populate all form fields. You can use this to quickly fill out the cover letter information.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="jsonInput">JSON Data</Label>
                  <Textarea
                    id="jsonInput"
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    rows={12}
                    className="font-mono text-sm"
                    placeholder="Paste your JSON data here..."
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleJsonImport}
                    disabled={!jsonInput.trim()}
                    className="flex-1"
                  >
                    <FileUp className="w-4 h-4 mr-2" />
                    Import JSON Data
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setJsonInput(getJsonTemplate())}
                  >
                    Load Template
                  </Button>
                </div>
                
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Expected JSON format:</p>
                  <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
{`{
  "headerInfo": {
    "fullName": "Your Name",
    "phoneNumber": "(555) 123-4567",
    "email": "your.email@example.com",
    "cityST": "City, State",
    "linkedin": "LinkedIn URL",
    "companyName": "Company Name",
    "recipientName": "Recipient Name",
    "jobTitle": "Job Title"
  },
  "letterBody": "Your cover letter content...",
  "templateUrl": "optional-template-url"
}`}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="generate" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>
                  Enter your contact information for the header
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      value={headerInfo.fullName}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phoneNumber">Phone Number *</Label>
                    <Input
                      id="phoneNumber"
                      value={headerInfo.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={headerInfo.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Your email address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cityST">City, State</Label>
                    <Input
                      id="cityST"
                      value={headerInfo.cityST}
                      onChange={(e) => handleInputChange('cityST', e.target.value)}
                      placeholder="Your location"
                    />
                  </div>
                  <div>
                    <Label htmlFor="linkedin">LinkedIn URL</Label>
                    <Input
                      id="linkedin"
                      value={headerInfo.linkedin}
                      onChange={(e) => handleInputChange('linkedin', e.target.value)}
                      placeholder="Your LinkedIn profile URL"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Job Information</CardTitle>
                <CardDescription>
                  Enter details about the position you're applying for
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="jobTitle">Job Title</Label>
                    <Input
                      id="jobTitle"
                      value={headerInfo.jobTitle}
                      onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                      placeholder="Position you're applying for"
                    />
                  </div>
                  <div>
                    <Label htmlFor="companyName">Company Name</Label>
                    <Input
                      id="companyName"
                      value={headerInfo.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      placeholder="Target company name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="recipientName">Recipient Name</Label>
                    <Input
                      id="recipientName"
                      value={headerInfo.recipientName}
                      onChange={(e) => handleInputChange('recipientName', e.target.value)}
                      placeholder="Recipient name"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cover Letter Content</CardTitle>
                <CardDescription>
                  Write or customize your cover letter body content.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={letterBody}
                  onChange={(e) => setLetterBody(e.target.value)}
                  rows={15}
                  className="font-mono text-sm"
                  placeholder="Enter your cover letter content here..."
                />
              </CardContent>
            </Card>

            {/* Credit Cost Display */}
            {user && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-primary" />
                      <span className="font-medium">Credit Cost:</span>
                      <span className="text-lg font-bold text-primary">{COVER_LETTER_CREDIT_COST} credits</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Your balance: <span className="font-semibold">{balance} credits</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-center">
              <Button 
                onClick={generateCoverLetter}
                disabled={generating || creditsLoading || (user && balance < COVER_LETTER_CREDIT_COST)}
                size="lg"
                className="w-full max-w-md"
              >
                {generating ? (
                  <>
                    <FileText className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : user && balance < COVER_LETTER_CREDIT_COST ? (
                  <>
                    <Coins className="w-4 h-4 mr-2" />
                    Insufficient Credits
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Generate Cover Letter ({COVER_LETTER_CREDIT_COST} credits)
                  </>
                )}
              </Button>
            </div>

            {/* Upgrade Prompt Modal */}
            {showUpgradePrompt && (
              <UpgradePrompt
                feature="Cover Letter Generation"
                creditsNeeded={COVER_LETTER_CREDIT_COST}
                onDismiss={() => setShowUpgradePrompt(false)}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}